import { supabase } from './supabase';
import { getSiteUrl } from './siteConfig';

export interface TokenBalance {
  available: number;
  totalPurchased: number;
  totalUsed: number;
  totalGifted: number;
}

export interface TokenUsageStats {
  last30Days: number;
  byDay: Record<string, number>;
}

export interface TokenPack {
  id: string;
  name: string;
  description: string | null;
  tokens_amount: number;
  price_cents: number;
  bonus_tokens: number;
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
}

export interface AIModel {
  id: string;
  name: string;
  description: string | null;
  token_multiplier: number;
  capabilities: string[];
  is_default: boolean;
}

export interface TokenBalanceResponse {
  success: boolean;
  balance: TokenBalance;
  usage: TokenUsageStats;
  models: AIModel[];
  packs: TokenPack[];
}

export interface PurchaseResponse {
  success: boolean;
  sessionId: string;
  sessionUrl: string;
  purchaseId: string;
  error?: string;
}

export interface ConfirmPurchaseResponse {
  success: boolean;
  alreadyProcessed: boolean;
  tokensAdded: number;
  newBalance: number;
  error?: string;
}

/**
 * Get token balance and usage stats for a business
 */
export async function getTokenBalance(businessId: string): Promise<TokenBalanceResponse> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await supabase.functions.invoke('ai-tokens-balance', {
    body: { businessId },
  });

  if (error) {
    console.error('Error fetching token balance:', error);
    throw new Error(error.message || 'Failed to fetch token balance');
  }

  return data;
}

/**
 * Create a checkout session for purchasing tokens
 */
export async function purchaseTokens(
  packId: string,
  businessId: string,
  userId?: string
): Promise<PurchaseResponse> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const baseUrl = getSiteUrl();

  const { data, error } = await supabase.functions.invoke('ai-tokens-purchase', {
    body: {
      packId,
      businessId,
      userId,
      successUrl: `${baseUrl}/ai-assistant?purchase=success`,
      cancelUrl: `${baseUrl}/ai-assistant?purchase=cancelled`,
    },
  });

  if (error) {
    console.error('Error creating purchase:', error);
    throw new Error(error.message || 'Failed to create purchase');
  }

  return data;
}

/**
 * Confirm a token purchase after Stripe redirect
 */
export async function confirmTokenPurchase(
  sessionId: string,
  purchaseId: string,
  businessId: string
): Promise<ConfirmPurchaseResponse> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await supabase.functions.invoke('ai-tokens-confirm', {
    body: {
      sessionId,
      purchaseId,
      businessId,
    },
  });

  if (error) {
    console.error('Error confirming purchase:', error);
    throw new Error(error.message || 'Failed to confirm purchase');
  }

  return data;
}

/**
 * Get token packs directly from database
 */
export async function getTokenPacks(): Promise<TokenPack[]> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await supabase
    .from('ai_token_packs')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching token packs:', error);
    throw new Error('Failed to fetch token packs');
  }

  return data || [];
}

/**
 * Format token count for display
 */
export function formatTokenCount(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toLocaleString();
}

/**
 * Format price in cents to dollars
 */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Calculate total tokens including bonus
 */
export function getTotalTokens(pack: TokenPack): number {
  return pack.tokens_amount + pack.bonus_tokens;
}

/**
 * Get price per token for a pack
 */
export function getPricePerToken(pack: TokenPack): number {
  const totalTokens = getTotalTokens(pack);
  return pack.price_cents / totalTokens / 100;
}






